"use client";

import Image from "next/image";
import { useState } from "react";

interface GalleryImage {
  _key?: string | null;
  asset?: { url?: string | null } | null;
}

interface ImageGalleryProps {
  images: GalleryImage[];
  name: string;
  noImageText?: string;
}

export function ImageGallery({ images, name, noImageText = "No image" }: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeUrl = images[activeIndex]?.asset?.url ?? "";

  return (
    <div className="space-y-3">
      {/* Main image */}
      <div className="relative aspect-square w-full overflow-hidden rounded-[1.75rem] bg-[#E9E4DB]">
        {activeUrl ? (
          <Image
            key={activeUrl}
            src={activeUrl}
            alt={name}
            fill
            priority
            className="object-cover transition-opacity duration-300"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-[#666058]">
            {noImageText}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
          {images.map((img, i) => {
            const url = img.asset?.url ?? "";
            if (!url) return null;
            const isActive = i === activeIndex;
            return (
              <button
                key={img._key ?? i}
                onClick={() => setActiveIndex(i)}
                aria-label={`${name} — image ${i + 1}`}
                aria-pressed={isActive}
                className={`relative aspect-square overflow-hidden rounded-[0.75rem] bg-[#E9E4DB] transition-all duration-200 ${
                  isActive
                    ? "ring-2 ring-[#4F4A43] ring-offset-2"
                    : "opacity-60 hover:opacity-100"
                }`}
              >
                <Image src={url} alt={`${name} ${i + 1}`} fill loading="lazy" className="object-cover" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
